import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext } from '@dnd-kit/sortable';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { useCallback, useRef, useState } from 'react';
import { bloxValues } from 'themes/constants';
import { hasLength } from 'utility/functions';
import { PlusNavObj } from '../types';
import DnDBasePlusNavItem from './DnDBasePlusNavItem';

interface PinnedAppsProps {
  allApps: PlusNavObj[];
  pinnedApps: string[];
  handleItemClick: (event: React.SyntheticEvent<HTMLElement>, item: PlusNavObj) => void;
  handlePinToggle: (item: PlusNavObj) => void;
  editMode: boolean;
  handleReorderApps: (oldIndex: number, newIndex: number) => void;
}

const PinnedApps = ({
  allApps,
  pinnedApps,
  handleItemClick,
  handlePinToggle,
  editMode,
  handleReorderApps,
}: PinnedAppsProps) => {
  const theme = useTheme();
  const dndBoxRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 0.1 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: 0.1 },
    }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = useCallback((event: any) => {
    setActiveId(event?.active?.id);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (active?.id !== over?.id) {
        const oldIndex = pinnedApps.indexOf(String(active?.id));
        const newIndex = pinnedApps.indexOf(String(over?.id));
        handleReorderApps(oldIndex, newIndex);
      }
    },
    [pinnedApps, handleReorderApps],
  );

  // call parent handler on enter key, but let spacebar be dnd kit mode
  const handleKeyDownCap = useCallback(
    (event: React.KeyboardEvent<HTMLElement>, item: PlusNavObj) => {
      if (event.key === 'Enter') {
        handleItemClick(event, item); // Navigate to the item's path or action on enter key
      }
    },
    [handleItemClick],
  );

  const activeItem = allApps.find((item) => item.key === activeId);

  // Resolve the ordered pin keys to actual items. A key can fail to resolve when
  // it's a template pinned on a different site (intentionally hidden here), so we
  // base the empty state on what actually renders rather than the raw key count.
  const visiblePinned = pinnedApps
    .map((key) => allApps.find((app) => app.key === key))
    .filter((item): item is PlusNavObj => Boolean(item));

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      autoScroll={true}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext items={pinnedApps}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            data-testid="plus-button-pinned-header"
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: bloxValues.fontWeightMedium,
              fontSize: bloxValues.regularFontSizeRem,
              color: theme.palette.text.secondary,
            }}
          >
            Pinned
          </Typography>

          <Box
            data-testid="plus-button-pinned-divider"
            id="plus-button-pinned-divider"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          />

          {editMode && (
            <Typography
              data-testid="plus-button-pinned-helpertext"
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: bloxValues.fontWeightRegular,
                fontSize: bloxValues.tinyFontSizeRem,
                color: theme.palette.text.secondary,
              }}
            >
              drag and drop to reorder
            </Typography>
          )}
        </Stack>

        <Box
          id="plus-button-pinned-grid"
          data-testid="plus-button-pinned-grid"
          ref={dndBoxRef}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
            gridAutoRows: 'minmax(80px, 1fr)', // equal height rows
            gap: 1,
            justifyItems: 'center',
            alignItems: 'center',
            borderRadius: 1,
            touchAction: 'none', // Prevent native touch pan/scroll from bleeding through during drag
          }}
        >
          {hasLength(visiblePinned) ? (
            visiblePinned.map((item) => (
              <DnDBasePlusNavItem
                key={item.key}
                id={`plus-button-pinned-item-${item.key}`}
                navItem={item}
                handleOnClick={(event) => handleItemClick(event, item)}
                asIcon={true}
                handlePinToggle={handlePinToggle}
                pinnedApps={pinnedApps}
                hidePin={!editMode}
                dndContainerRef={dndBoxRef}
                fullHeight={true}
                fullWidth={true}
                onKeyDownCapture={(event) => handleKeyDownCap(event, item)}
                href={item?.path}
                hidden={item?.hidden}
              />
            ))
          ) : (
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: bloxValues.tinyFontSizeRem,
                border: `2px solid ${theme.palette.action.hover}`,
                backgroundColor: theme.palette.action.hover,
                p: 2,
                width: '100%',
                borderRadius: 1,
                gridColumn: '1 / -1',
                textAlign: 'center',
              }}
            >
              Search and pin items for quick access...
            </Typography>
          )}
        </Box>
      </SortableContext>

      <DragOverlay>
        {activeItem ? (
          <DnDBasePlusNavItem
            navItem={activeItem}
            handleOnClick={() => {}} // No-op for the overlay
            asIcon={true}
            hidePin={true}
            fullHeight={true}
            fullWidth={true}
            sx={{
              minWidth: '83px',
              minHeight: '83px',
            }}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

PinnedApps.displayName = 'PinnedApps';
export default PinnedApps;
